/**
 * Document Viewer Component (Center Panel)
 *
 * Displays the current document with:
 * - Document navigation and selection
 * - Progress bar (coded chunks / total)
 * - Scrollable chunk list
 * - Applied code chips per chunk
 * - Chunk selection state
 */

import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  LinearProgress,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  keyframes,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  VALENCE_CHIP_COLORS,
  AI_GHOST_COLORS,
  CHUNK_STATES,
  DOCUMENT_STATUS,
} from '../../constants/workbenchConfig';

// Pulse animation for AI ghost pills
const ghostPulse = keyframes`
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
`;

// Pop-in animation for newly applied code pills
const pillPopIn = keyframes`
  0% { opacity: 0; transform: scale(0.8); }
  60% { transform: scale(1.05); }
  100% { opacity: 1; transform: scale(1); }
`;

/**
 * Document Viewer Panel
 */
// eslint-disable-next-line max-lines-per-function
function DocumentViewer({
  documents,
  currentDocIndex,
  onSelectDocument,
  onNextDocument,
  onPrevDocument,
  chunks,
  selectedChunkIndex,
  onSelectChunk,
  progress,
  loading,
  error,
  onRemoveCode,
  aiSuggestions = [],
  onAcceptSuggestion,
}) {

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load documents: {error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Document Toolbar */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'background.paper',
        }}
      >
        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Previous document (Shift+P)">
            <span>
              <IconButton
                size="small"
                onClick={onPrevDocument}
                disabled={currentDocIndex === 0}
              >
                <NavigateBeforeIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Next document (Shift+N)">
            <span>
              <IconButton
                size="small"
                onClick={onNextDocument}
                disabled={currentDocIndex >= documents.length - 1}
              >
                <NavigateNextIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Document Selector */}
        <Select
          value={currentDocIndex}
          onChange={(e) => onSelectDocument(e.target.value)}
          size="small"
          sx={{ minWidth: 250, flex: 1 }}
          disabled={documents.length === 0}
        >
          {documents.length === 0 ? (
            <MenuItem value={0}>No documents available</MenuItem>
          ) : (
            documents.map((doc, index) => (
              <MenuItem key={doc.sourceId || index} value={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {doc.title || doc.sourceId || `Document ${index + 1}`}
                  </Typography>
                  {doc.status && (
                    <StatusBadge status={doc.status} />
                  )}
                </Box>
              </MenuItem>
            ))
          )}
        </Select>

        {/* Progress Indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {progress.coded}/{progress.total} coded
          </Typography>
          <Box sx={{ width: 80 }}>
            <LinearProgress
              variant="determinate"
              value={progress.percentage}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'rgba(0,0,0,0.08)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'accent.teal',
                  borderRadius: 3,
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Chunk List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : chunks.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              {documents.length === 0
                ? 'No documents found for this entity'
                : 'Select a document to view chunks'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {chunks.map((chunk, index) => (
              <ChunkCard
                key={chunk.id || index}
                chunk={chunk}
                index={index}
                isSelected={index === selectedChunkIndex}
                onClick={() => onSelectChunk(index)}
                onRemoveCode={onRemoveCode}
                aiSuggestions={index === selectedChunkIndex ? aiSuggestions : []}
                onAcceptSuggestion={onAcceptSuggestion}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * Status Badge Component
 */
function StatusBadge({ status }) {
  const config = DOCUMENT_STATUS[status] || DOCUMENT_STATUS.new;

  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        height: 20,
        fontSize: '0.65rem',
        fontWeight: 500,
        bgcolor: config.bgColor,
        color: config.color,
        border: 'none',
      }}
    />
  );
}

/**
 * Chunk Card Component (mockup-aligned design)
 */
// eslint-disable-next-line max-lines-per-function
function ChunkCard({ chunk, index, isSelected, onClick, onRemoveCode, aiSuggestions = [], onAcceptSuggestion }) {
  const appliedCodes = chunk.behavioral_codes || [];
  const hasCodes = appliedCodes.length > 0;
  const hasGhostPills = isSelected && aiSuggestions.length > 0;
  const chunkId = chunk.id || chunk.chunkId;

  // Determine chunk state for styling
  const state = isSelected
    ? CHUNK_STATES.selected
    : hasCodes
    ? CHUNK_STATES.coded
    : CHUNK_STATES.default;

  // Circular badge color based on state
  const badgeStyles = isSelected
    ? { bgcolor: 'primary.main', color: '#fff' }
    : hasCodes
    ? { bgcolor: '#23A38F', color: '#fff' }
    : { bgcolor: 'grey.100', color: 'text.secondary' };

  // Handle keyboard interaction for accessibility
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <Paper
      elevation={isSelected ? 2 : 0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-pressed={isSelected}
      aria-label={`Chunk ${index + 1}${chunk.speaker ? `, speaker: ${chunk.speaker}` : ''}${hasCodes ? `, ${appliedCodes.length} codes applied` : ''}`}
      sx={{
        p: 2,
        pl: 2.5,
        cursor: 'pointer',
        position: 'relative',
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: state.borderLeft,
        bgcolor: state.bg,
        borderRadius: 2,
        transition: 'all 0.15s ease',
        '&:hover, &:focus': {
          borderLeftColor: isSelected
            ? state.borderLeft
            : 'rgba(20, 27, 45, 0.3)',
          outline: 'none',
        },
        '&:hover .source-btn': {
          opacity: 1,
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      {/* Chunk Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Circular number badge */}
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
              fontWeight: 700,
              lineHeight: 1,
              flexShrink: 0,
              ...badgeStyles,
            }}
          >
            {index + 1}
          </Box>
          {chunk.speaker && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <PersonIcon sx={{ fontSize: 14 }} />
              {chunk.speaker}
            </Typography>
          )}
        </Box>

        {/* Source button — hidden by default, shown on hover */}
        <Box
          className="source-btn"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            opacity: 0,
            transition: 'opacity 0.2s ease',
          }}
        >
          {chunk.sourceUrl && (
            <Tooltip title="Open source media">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(chunk.sourceUrl, '_blank', 'noopener,noreferrer');
                }}
              >
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      {/* Chunk Content */}
      <Typography
        variant="body2"
        sx={{
          color: 'text.primary',
          lineHeight: 1.6,
          mb: (hasCodes || hasGhostPills) ? 1.5 : 0,
        }}
      >
        {chunk.content || chunk.text || 'No content'}
      </Typography>
      {/* Applied Codes — pastel pills */}
      {hasCodes && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: hasGhostPills ? 1 : 0 }}>
          {appliedCodes.map((code) => {
            const colors = VALENCE_CHIP_COLORS[code.valence] || VALENCE_CHIP_COLORS.neutral;
            const confidencePct = code.confidence != null
              ? `${Math.round(code.confidence * 100)}%`
              : null;
            return (
              <Chip
                key={code.code}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>{code.code.replace(/_/g, ' ')}</span>
                    {confidencePct && (
                      <span style={{ opacity: 0.7, fontSize: '0.6rem' }}>
                        {confidencePct}
                      </span>
                    )}
                  </Box>
                }
                size="small"
                onDelete={(e) => {
                  e.stopPropagation();
                  onRemoveCode(chunkId, code.code);
                }}
                deleteIcon={
                  <CloseIcon
                    sx={{
                      fontSize: '14px !important',
                      color: `${colors.text} !important`,
                      opacity: 0.5,
                      '&:hover': { opacity: 1 },
                    }}
                  />
                }
                sx={{
                  height: 24,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  bgcolor: colors.bg,
                  color: colors.text,
                  border: '1px solid',
                  borderColor: colors.border,
                  animation: `${pillPopIn} 0.25s ease-out`,
                  '& .MuiChip-deleteIcon': {
                    color: colors.text,
                  },
                }}
              />
            );
          })}
        </Box>
      )}
      {/* AI Ghost Pills — shown inline when chunk is selected */}
      {hasGhostPills && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {aiSuggestions.map((suggestion) => (
            <GhostPill
              key={suggestion.code}
              suggestion={suggestion}
              onAccept={onAcceptSuggestion}
            />
          ))}
        </Box>
      )}
    </Paper>
  );
}

/**
 * AI Ghost Pill — dashed-border teal pill with pulse animation, click to apply
 */
function GhostPill({ suggestion, onAccept }) {
  return (
    <Chip
      icon={<AutoAwesomeIcon sx={{ fontSize: '14px !important', color: 'inherit' }} />}
      label={suggestion.code.replace(/_/g, ' ')}
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        if (onAccept) onAccept(suggestion);
      }}
      sx={{
        height: 24,
        fontSize: '0.7rem',
        fontWeight: 500,
        bgcolor: AI_GHOST_COLORS.bg,
        color: AI_GHOST_COLORS.text,
        border: '1px dashed',
        borderColor: AI_GHOST_COLORS.border,
        cursor: 'pointer',
        animation: `${ghostPulse} 2s ease-in-out infinite`,
        '&:hover': {
          animation: 'none',
          bgcolor: AI_GHOST_COLORS.hoverBg,
          color: AI_GHOST_COLORS.hoverText,
          borderStyle: 'solid',
          '& .MuiChip-icon': {
            color: `${AI_GHOST_COLORS.hoverText} !important`,
          },
        },
        '& .MuiChip-icon': {
          color: AI_GHOST_COLORS.text,
          ml: '4px',
        },
      }}
    />
  );
}

export default DocumentViewer;
